import type { paths } from "../types/fulfilment-api.generated";
import * as api from "./client";

// ============================================================================
// Type Definitions from OpenAPI spec
// ============================================================================

export type SubscriptionsListResponse =
  paths["/api/v1/subscriptions"]["get"]["responses"]["200"]["content"]["application/json"];

export type SubscriptionDetailResponse =
  paths["/api/v1/subscriptions/{id}"]["get"]["responses"]["200"]["content"]["application/json"];

export type AvailableEditionsResponse =
  paths["/api/v1/subscriptions/{id}/available-editions"]["get"]["responses"]["200"]["content"]["application/json"];

export type AvailableEdition = AvailableEditionsResponse["editions"][number];

export type SubscriptionQueryParams = Omit<
  NonNullable<paths["/api/v1/subscriptions"]["get"]["parameters"]["query"]>,
  "fullyConfigured"
> & {
  crmAccountId?: string;
  slug?: string;
  siteStructureId?: string;
  editorialCategoryId?: number;
  fullyConfigured?: boolean;
};

export type SubscriptionBase = SubscriptionsListResponse["subscriptions"][0];

export type Subscription = SubscriptionBase & {
  companyName?: string | null;
  companySlug?: string | null;
  companyCrmEntityNumber?: string | null;
};

export type SubscriptionWithFeatures = SubscriptionDetailResponse["subscription"] & {
  companyName?: string | null;
  companySlug?: string | null;
  companyCrmEntityNumber?: string | null;
};

export type SubscriptionsListWithPagination = {
  subscriptions: Subscription[];
  pagination: SubscriptionsListResponse["pagination"];
};

// ============================================================================
// Service
// ============================================================================

export const subscriptions = {
  getAll: async (params?: SubscriptionQueryParams): Promise<SubscriptionsListWithPagination> => {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set("search", params.search);
    if (params?.crmAccountId) searchParams.set("crmAccountId", params.crmAccountId);
    if (params?.slug) searchParams.set("slug", params.slug);
    if (params?.siteStructureId) searchParams.set("siteStructureId", params.siteStructureId);
    if (params?.editorialCategoryId)
      searchParams.set("editorialCategoryId", String(params.editorialCategoryId));
    if (params?.status) searchParams.set("status", params.status);
    if (params?.productCode) searchParams.set("productCode", params.productCode);
    if (params?.expiringInDays !== undefined)
      searchParams.set("expiringInDays", String(params.expiringInDays));
    if (params?.fullyConfigured !== undefined)
      searchParams.set("fullyConfigured", String(params.fullyConfigured));
    if (params?.sortField) searchParams.set("sortField", params.sortField);
    if (params?.sortDirection) searchParams.set("sortDirection", params.sortDirection);
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.offset) searchParams.set("offset", String(params.offset));

    const queryString = searchParams.toString();
    const url = `/v1/subscriptions-enriched${queryString ? `?${queryString}` : ""}`;

    const response = await api.get<SubscriptionsListResponse>(url);
    return {
      subscriptions: response.subscriptions,
      pagination: response.pagination,
    };
  },

  getById: async (id: string, includeFeatures = false): Promise<SubscriptionWithFeatures> => {
    const query = includeFeatures ? "?includeFeatures=true" : "";
    const response = await api.get<SubscriptionDetailResponse>(
      `/v1/fulfilment/subscriptions/${id}${query}`,
    );
    return response.subscription;
  },

  create: async (data: {
    firmId: string;
    packageId: string;
    billingTaxonomyTermId: string;
    siteStructureId: string;
    site: string;
    language: string;
    startDate: string;
    endDate: string;
    status?: string;
    correlationId?: string;
    metadata?: Record<string, unknown>;
  }): Promise<Subscription> => {
    const response = await api.post<SubscriptionDetailResponse>(
      "/v1/fulfilment/subscriptions",
      data,
    );
    return response.subscription;
  },

  update: async (
    id: string,
    data: {
      firmId?: string;
      packageId?: string;
      siteStructureId?: string;
      site?: string | null;
      language?: string | null;
      startDate?: string;
      endDate?: string;
      status?: string;
      metadata?: Record<string, unknown>;
    },
  ): Promise<Subscription> => {
    const response = await api.patch<SubscriptionDetailResponse>(
      `/v1/fulfilment/subscriptions/${id}`,
      data,
    );
    return response.subscription;
  },

  cancel: async (id: string): Promise<void> => {
    await api.del(`/v1/fulfilment/subscriptions/${id}`);
  },

  getAvailableEditions: async (id: string): Promise<AvailableEdition[]> => {
    const response = await api.get<AvailableEditionsResponse>(
      `/v1/fulfilment/subscriptions/${id}/available-editions`,
    );
    return response.editions;
  },

  getActiveCountByProductCode: async (productCode: string): Promise<number> => {
    const [pendingResponse, completeResponse] = await Promise.all([
      subscriptions.getAll({ productCode, status: "pending", limit: 1 }),
      subscriptions.getAll({ productCode, status: "complete", limit: 1 }),
    ]);

    return pendingResponse.pagination.total + completeResponse.pagination.total;
  },
};
